import { useInitialLoading } from '../hooks/useInitialLoading';
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getAuthHeaders } from '../utils/authHeaders';

export default function ProjectFinancial() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const companyId = localStorage.getItem('selectedCompanyId');
  const API = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);
  const [linkInput, setLinkInput] = useState('');

  const load = useCallback(() => {
    if (!companyId || !projectId) return;
    setLoading(true);
    fetch(`${API}/companies/${companyId}/projects/${projectId}/financial`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : null)
      .then(d => setData(d))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [companyId, projectId, API]);

  useEffect(() => { load(); }, [load]);

  const linkFinancial = () => {
    if (!linkInput) { window.alert('Enter a Financial Project ID'); return; }
    fetch(`${API}/companies/${companyId}/projects/${projectId}/financial/link`, {
      method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ financial_project_id: linkInput })
    }).then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(() => { setLinkInput(''); load(); })
      .catch(() => window.alert('Failed to link financial project'));
  };

  const unlinkFinancial = () => {
    if (!window.confirm('Unlink financial project?')) return;
    fetch(`${API}/companies/${companyId}/projects/${projectId}/financial/unlink`, {
      method: 'POST', headers: getAuthHeaders()
    }).then(r => r.ok ? r.json() : null).then(() => load()).catch(() => {});
  };

  if (!companyId) return <div style={pageStyle}><p>Please select a company.</p></div>;
  if (initialLoading) return <div style={pageStyle}><p>Loading...</p></div>;

  const labor = data?.labor_cost || {};
  const fin = data?.financial_data || null;
  const linked = !!data?.financial_project_id;

  return (
    <div style={pageStyle}>
      <div style={{ marginBottom: '16px' }}>
        <button onClick={() => navigate(`/company/projects/${projectId}`)} style={linkBtn}>← Back to Project</button>
        <h2 style={{ margin: '4px 0 0', fontSize: '1.4rem', fontWeight: 700 }}>💰 Financial Summary</h2>
        <p style={{ margin: '4px 0 0', fontSize: '0.8rem', color: '#94a3b8' }}>Read-only financial view — labor costs from time tracking + financial data from Financial Management</p>
      </div>

      {/* Labor Costs from Time Tracking */}
      <div style={{ ...cardStyle, marginBottom: '20px' }}>
        <h3 style={{ margin: '0 0 16px', fontSize: '1rem', fontWeight: 600 }}>⏱️ Labor Costs (from Time Tracking)</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '12px', marginBottom: '16px' }}>
          <div style={statCard}><div style={statLabel}>Total Hours</div><div style={{ fontSize: '1.3rem', fontWeight: 700, color: '#3b82f6' }}>{labor.total_hours || '0.00'}</div></div>
          <div style={statCard}><div style={statLabel}>Total Labor Cost</div><div style={{ fontSize: '1.3rem', fontWeight: 700, color: '#22c55e' }}>${labor.total_cost || '0.00'}</div></div>
          <div style={statCard}><div style={statLabel}>Approved Hours</div><div style={{ fontSize: '1.3rem', fontWeight: 700, color: '#8b5cf6' }}>{labor.approved_hours || '0.00'}</div></div>
          <div style={statCard}><div style={statLabel}>Pending Hours</div><div style={{ fontSize: '1.3rem', fontWeight: 700, color: '#f59e0b' }}>{labor.pending_hours || '0.00'}</div></div>
        </div>

        {Array.isArray(labor.by_user) && labor.by_user.length > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Team Member</th>
                  <th style={thStyle}>Hours</th>
                  <th style={thStyle}>Rate</th>
                  <th style={thStyle}>Cost</th>
                </tr>
              </thead>
              <tbody>
                {labor.by_user.map((u, i) => (
                  <tr key={i}>
                    <td style={tdStyle}>{u.user?.name || `User #${u.user_id}`}</td>
                    <td style={tdStyle}>{u.total_hours}</td>
                    <td style={tdStyle}>${u.hourly_rate || '-'}</td>
                    <td style={tdStyle}>${u.total_cost}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Financial Project Link */}
      <div style={{ ...cardStyle, marginBottom: '20px' }}>
        <h3 style={{ margin: '0 0 16px', fontSize: '1rem', fontWeight: 600 }}>📊 Financial Management Link</h3>
        {linked ? (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
              <span style={{ ...badge, backgroundColor: '#22c55e' }}>Linked</span>
              <span style={{ fontSize: '0.85rem' }}>Financial Project ID: <strong>{data.financial_project_id}</strong></span>
              <button onClick={unlinkFinancial} style={{ ...smallBtn, color: '#ef4444' }}>Unlink</button>
            </div>

            {fin ? (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '12px' }}>
                <div style={statCard}><div style={statLabel}>Budget</div><div style={{ fontSize: '1.3rem', fontWeight: 700, color: '#3b82f6' }}>${fin.budget || '0.00'}</div></div>
                <div style={statCard}><div style={statLabel}>Total Revenue</div><div style={{ fontSize: '1.3rem', fontWeight: 700, color: '#22c55e' }}>${fin.total_revenue || '0.00'}</div></div>
                <div style={statCard}><div style={statLabel}>Total Expenses</div><div style={{ fontSize: '1.3rem', fontWeight: 700, color: '#ef4444' }}>${fin.total_expenses || '0.00'}</div></div>
                <div style={statCard}><div style={statLabel}>Profit/Loss</div><div style={{ fontSize: '1.3rem', fontWeight: 700, color: (fin.profit_loss || 0) >= 0 ? '#22c55e' : '#ef4444' }}>${fin.profit_loss || '0.00'}</div></div>
                <div style={statCard}><div style={statLabel}>Budget Used</div><div style={{ fontSize: '1.3rem', fontWeight: 700, color: (fin.budget_utilization || 0) > 90 ? '#ef4444' : '#3b82f6' }}>{fin.budget_utilization || 0}%</div></div>
              </div>
            ) : (
              <p style={{ fontSize: '0.85rem', color: '#94a3b8' }}>Financial data unavailable. The Financial Management module may not be set up.</p>
            )}
          </div>
        ) : (
          <div>
            <p style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: '12px' }}>No financial project linked. Link one to view budget, revenue, and expense data.</p>
            <div style={{ display: 'flex', gap: '8px' }}>
              <input type="number" placeholder="Financial Project ID" value={linkInput} onChange={e => setLinkInput(e.target.value)} style={{ ...inputStyle, maxWidth: '200px' }} />
              <button onClick={linkFinancial} style={primaryBtn}>Link</button>
            </div>
          </div>
        )}
      </div>

      {/* Combined Summary */}
      {linked && fin && (
        <div style={{ ...cardStyle, borderLeft: '4px solid #3b82f6' }}>
          <h3 style={{ margin: '0 0 16px', fontSize: '1rem', fontWeight: 600 }}>📋 Combined Financial View</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '0.85rem' }}>
            <div style={{ padding: '8px 0', borderBottom: '1px solid #f1f5f9' }}><span style={{ color: '#64748b' }}>Budget:</span></div>
            <div style={{ padding: '8px 0', borderBottom: '1px solid #f1f5f9', fontWeight: 600 }}>${fin.budget || '0.00'}</div>
            <div style={{ padding: '8px 0', borderBottom: '1px solid #f1f5f9' }}><span style={{ color: '#64748b' }}>Revenue:</span></div>
            <div style={{ padding: '8px 0', borderBottom: '1px solid #f1f5f9', fontWeight: 600, color: '#22c55e' }}>${fin.total_revenue || '0.00'}</div>
            <div style={{ padding: '8px 0', borderBottom: '1px solid #f1f5f9' }}><span style={{ color: '#64748b' }}>Expenses (Financial):</span></div>
            <div style={{ padding: '8px 0', borderBottom: '1px solid #f1f5f9', fontWeight: 600, color: '#ef4444' }}>${fin.total_expenses || '0.00'}</div>
            <div style={{ padding: '8px 0', borderBottom: '1px solid #f1f5f9' }}><span style={{ color: '#64748b' }}>Labor Cost (Time Tracking):</span></div>
            <div style={{ padding: '8px 0', borderBottom: '1px solid #f1f5f9', fontWeight: 600, color: '#f59e0b' }}>${labor.total_cost || '0.00'}</div>
            <div style={{ padding: '8px 0', borderBottom: '1px solid #f1f5f9' }}><span style={{ color: '#64748b' }}>Total Cost (Expenses + Labor):</span></div>
            <div style={{ padding: '8px 0', borderBottom: '1px solid #f1f5f9', fontWeight: 700, color: '#ef4444' }}>${((parseFloat(fin.total_expenses) || 0) + (parseFloat(labor.total_cost) || 0)).toFixed(2)}</div>
          </div>
        </div>
      )}
    </div>
  );
}

const pageStyle = { padding: '24px', maxWidth: '1400px', margin: '0 auto' };
const cardStyle = { backgroundColor: '#fff', borderRadius: '8px', padding: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0' };
const statCard = { backgroundColor: '#fff', borderRadius: '8px', padding: '12px 16px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0' };
const statLabel = { fontSize: '0.75rem', color: '#64748b' };
const tableStyle = { width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' };
const thStyle = { textAlign: 'left', padding: '8px 10px', borderBottom: '2px solid #e2e8f0', fontSize: '0.75rem', fontWeight: 600, color: '#64748b', textTransform: 'uppercase' };
const tdStyle = { padding: '8px 10px', borderBottom: '1px solid #f1f5f9', fontSize: '0.85rem' };
const badge = { display: 'inline-block', padding: '2px 8px', borderRadius: '10px', fontSize: '0.68rem', color: '#fff', fontWeight: 600, textTransform: 'capitalize' };
const inputStyle = { padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.85rem', width: '100%', boxSizing: 'border-box' };
const primaryBtn = { padding: '8px 16px', backgroundColor: '#3b82f6', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem' };
const smallBtn = { padding: '4px 8px', backgroundColor: '#f1f5f9', color: '#334155', border: '1px solid #e2e8f0', borderRadius: '4px', cursor: 'pointer', fontSize: '0.75rem' };
const linkBtn = { background: 'none', border: 'none', color: '#3b82f6', cursor: 'pointer', fontSize: '0.85rem', padding: 0 };
